/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import Link from '@react/react-spectrum/Link';
import * as URL from 'url';
import { addClass } from '../../../../utils/cssUtils';
import { PDPAnalytics, BannerAnalytics } from '../../../../analytics';
import Banner from '../../../../components/banner';
import BannerModel from '../../../../stores/banner/banner';
import { ANALYTICS } from '../../../../utils/enums';
import LoggerUtil from '../../../../utils/LoggerUtil';
import ContentParser from '../../../../utils/contentParser';
import { BANNER_LAYOUTS, BANNER_ITEM_LAYOUTS } from '../../../layoutConstants';
import BannerItem from '../../../../stores/banner/bannerItem';
import { PDP_SECTION, PDP_ACTION } from '../../../../utils/pdpUtils/pdpEnums';
import TranslationUtil from '../../../../utils/translation';

class OverviewKeyFeatures extends Component {
    componentDidMount() {
        if (this.overviewData) {
            if (this.showExpandedKeyfeatures) {
                this.expandOverview();
            }
        }
    }

    onViewMoreClick(e) {
        const { PDPDataObject: { actionHandlerCallback } } = this.props;
        const { viewMoreKeyFeatures } = this.overviewData;
        const guid = uuid.v4();
        const actionParam = {
            actionObject: viewMoreKeyFeatures,
            guid,
        };
        this.expandOverview();
        actionHandlerCallback(
            e, PDP_SECTION.OVERVIEW, PDP_ACTION.VIEW_MORE_CLICK, actionParam
        );

        PDPAnalytics.viewMoreKeyFeaturesClick(viewMoreKeyFeatures, guid);
    }

    getBannerModel(contentfulPDPItems, supportedVideoFormats,
        supportedVideoDomains, localAssetsBasePath) {
        const { bannerImage, bannerVideo } = contentfulPDPItems;
        const { PDPDataObject: { actionHandlerCallback } } = this.props;
	    let bannerItem;

	    const videoList = [];
	    if (bannerVideo) {
	        supportedVideoFormats.forEach((format) => {
	            if (Object.prototype.hasOwnProperty.call(bannerVideo, format)) {
	                // [CCD-2930] Video should be served only through supported URLs (adobe.com)
	                const videoUrl = bannerVideo[format];
	                const parsedURL = URL.parse(videoUrl);
	                let isValidDomain = false;

	                if (supportedVideoDomains
						&& Array.isArray(supportedVideoDomains)
						&& supportedVideoDomains.length > 0) {
                        supportedVideoDomains.forEach((domain) => {
	                        const domainRegEx = new RegExp(`.*\.${domain}$`);
	                        if (domainRegEx.test(parsedURL.hostname)
								|| parsedURL.hostname === domain) {
	                            isValidDomain = true;
	                        }
	                    });
	                } else {
	                    isValidDomain = true;
	                }

	                if (isValidDomain) {
	                    videoList.push({
	                        type: `video/${format}`,
	                        src: videoUrl,
	                    });
	                } else {
		                LoggerUtil.warn(`Invalid video URL: ${videoUrl}`);
	                }
	            }
	        });
        }

        if (videoList.length > 0
                && ContentParser
                    .getAssetPath(bannerVideo.thumbnailImage, localAssetsBasePath)) {
	        bannerItem = new BannerItem({
	            layoutId: BANNER_ITEM_LAYOUTS.VIDEO_ONLY,
                thumbNailImg: ContentParser
                    .getAssetPath(bannerVideo.thumbnailImage, localAssetsBasePath),
                videoList,
                onAction: actionParam => actionHandlerCallback(
                    {},
                    PDP_SECTION.OVERVIEW,
                    PDP_ACTION.VIDEO_BANNER_CLICK,
                    actionParam
                ),
	        });
        } else if (bannerImage && ContentParser
            .getAssetPath(bannerImage, localAssetsBasePath)) {
	        bannerItem = new BannerItem({
	            layoutId: BANNER_ITEM_LAYOUTS.IMAGE_ONLY,
	            imageList: [
	                ContentParser.getAssetPath(bannerImage, localAssetsBasePath),
	            ],
	        });
	    }
	    if (bannerItem) {
	        return new BannerModel({
	            layoutId: BANNER_LAYOUTS.FULL_WIDTH,
	            bannerItemList: [
	                bannerItem,
	            ],
	        });
	    }
	    return null;
    }

    get overviewData() {
        const { PDPDataObject } = this.props;
        return this.validateAndProcessData(PDPDataObject);
    }

    setShowExpandedKeyfeatures() {
        const { PDPDataObject, showExpandedKeyfeatures } = this.props;
        const { contentfulData } = PDPDataObject;
        this.showExpandedKeyfeatures = showExpandedKeyfeatures;
        if ((!contentfulData.behanceList
			|| contentfulData.behanceList.length === 0)
			&& (!contentfulData.tutorialList
			|| contentfulData.tutorialList.length === 0)) {
            this.showExpandedKeyfeatures = true;
        }
    }

    // eslint-disable-next-line react/sort-comp
    validateAndProcessData({
        contentfulData, bannerConfig, localAssetsBasePath,
    }) {
        const { supportedVideoFormats, supportedVideoDomains } = bannerConfig;
        const banner = this.getBannerModel(contentfulData,
	        supportedVideoFormats, supportedVideoDomains, localAssetsBasePath);
	    // overview title
	    const { overviewTitle } = contentfulData;
        // Object consisting of sectionId and actionId

	    const overviewText = ContentParser.processText(contentfulData.overview.plaintext
            ? contentfulData.overview.plaintext : contentfulData.overview);

	    const viewMoreKeyFeatures = {
	        sectionId: PDP_SECTION.OVERVIEW,
	        actionId: PDP_ACTION.VIEW_MORE_CLICK,
        };

        const keyFeaturesList = contentfulData.keyFeatureList;

        return {
            banner,
            overviewTitle,
            overviewText,
            viewMoreKeyFeatures,
            keyFeaturesList,
        };
    }

    expandOverview() {
        const keyFeaturesSection = document.getElementById('key-features-section');
        const viewMoreLink = document.getElementById('view-more');
        addClass(keyFeaturesSection, 'expand-key-features-section');
        addClass(viewMoreLink, 'hide-view-more-text');
    }

    renderKeyFeatures(keyFeaturesList) {
        const bulletList = [];
        keyFeaturesList.forEach((feature) => {
            bulletList.push(
                <li className="feature" key={feature.title.replace(/ /g, '_')}>
                    <div className="feature-heading">{feature.title}</div>
                    <div className="feature-text">{feature.description.plaintext ? feature.description.plaintext : feature.description }</div>
                </li>
            );
        });
        return	bulletList;
    }

    render() {
        if (!this.overviewData) {
            return null;
        }

        const {
            layouts,
	        appConfig,
	        appsPanelSkeleton,
        } = this.props;
        this.setShowExpandedKeyfeatures();
	    const { keyFeaturesList } = this.overviewData;
        const spaceText = ' ';
        /**
         * Set banner source = pdp for analytics
         */
        BannerAnalytics.setBannerSource(ANALYTICS.PDP);
        return (
            <div className="overview-key-features left-panel-compt">
                { this.overviewData.banner
                && (
                    <div className="banner">
                        <Banner
                            banner={this.overviewData.banner}
                            tabIndex={0}
                            appConfig={appConfig}
                            appsPanelSkeleton={appsPanelSkeleton}
                            bannerLayouts={layouts} />
                    </div>
                )}

                <h1 className="overview-key-features-heading">{this.overviewData.overviewTitle}</h1>

                <div className="overview-text">
                    <span>{this.overviewData.overviewText}</span>
                    { this.overviewData.viewMoreKeyFeatures
                        && !this.showExpandedKeyfeatures
                        && (
                            <span>
                                <span>{spaceText}</span>
                                <Link
                                    tabIndex={0}
                                    role="button"
                                    aria-label="View More"
                                    onKeyDown={(e) => {
                                        if (e.keyCode === 13) {
                                            this.onViewMoreClick(e);
                                        }
                                    }}
                                    onClick={(e) => { this.onViewMoreClick(e); }}
                                    id="view-more">
                                    {TranslationUtil.getMessage('ccx.pdp.view-more')}
                                </Link>
                            </span>
                        )
                    }
                </div>
                { this.overviewData.viewMoreKeyFeatures && <ul id="key-features-section" className="key-features-section">{this.renderKeyFeatures(keyFeaturesList)}</ul> }
            </div>
        );
    }
}

export default observer(OverviewKeyFeatures);
